import { StyleSheet } from 'aphrodite';
import { addAlphaToHex } from '../../../../../../models/UiUtils';
import { THEME } from '../../../../../../web/components-v2/Theme/theme';
import { brandPrimary, charmGray, destructive, successGreen, titles } from '../../../../../../web/styles/colors';
import { CssSelectors } from '../../../../../../web/styles/styles';

const { colors, spacing, common } = THEME;

export const styleSheet = StyleSheet.create({
	section: {
		borderRadius: 3,
		boxSizing: 'border-box',
		padding: `${spacing.md}px ${spacing.md}px 0`,
		width: 452,
	},
	sectionNoBorderTop: {
		borderTop: spacing.none,
	},
	borderRightBottom: {
		borderRight: common.border,
		borderBottom: common.border,
		borderTopRightRadius: 0,
		borderBottomRightRadius: common.borderRadius,
	},
	sectionTitle: {
		color: titles,
	},
	bottomSection: {
		background: colors.background.light,
		width: '100%',
		boxSizing: 'border-box',
		position: 'relative',
		marginBottom: spacing.md,
		borderTopLeftRadius: common.borderRadius,
		borderBottomLeftRadius: common.borderRadius,

		...CssSelectors.allChildren(
			{
				padding: `${spacing.md}px ${spacing.md}px 0`,
				...CssSelectors.allChildren(
					{
						margin: `0 0 ${spacing.md}px 0`,
					},
					'h3'
				),
			},
			'header'
		),
	},
	sectionFullWidth: {
		width: '100%',
		boxSizing: 'border-box',
		marginBottom: spacing.lg,
	},
	addQuestionButton: {
		padding: spacing.md,
		width: '100%',
		textAlign: 'center',
		color: brandPrimary,
		background: addAlphaToHex(brandPrimary, 0.1, '#FFFFFF'),
		border: `2px dashed ${brandPrimary}`,
		borderRadius: common.borderRadius,
	},
	/**
	 * Flex row wrapping the V1 "+ Add Question" button alongside the
	 * `SectionsActionBar`'s "+ Add Section" button. Each child claims an equal
	 * half of the row so the affordances sit side-by-side at the bottom of V1
	 * instead of stacking.
	 */
	bottomActionRow: {
		display: 'flex',
		gap: spacing.md,
		width: '100%',
		':nth-child(1n) > *': {
			flexGrow: 1,
			flexBasis: 0,
			minWidth: 0,
		},
	},
	tagPrefixContainer: {
		display: 'flex',
		gap: spacing.sm,
		alignItems: 'center',
		justifyContent: 'flex-end',
		borderRight: common.border,
		paddingRight: spacing.sm,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		...CssSelectors.allDescendants(
			{
				margin: 0,
				fontSize: THEME.typography.fontSizes.xs,
			},
			'p'
		),
	},
	removeQuestionButton: {
		transform: 'translateY(-60px)',
	},
	bottomSectionNotSaved: {
		borderLeft: `4px solid ${brandPrimary}`,
	},
	bottomSectionSaved: {
		borderLeft: `4px solid ${successGreen}`,
	},
	bottomSectionNoValue: {
		borderLeft: `4px solid ${destructive}`,
	},
	afterInputIcon: {
		top: 12,
		right: 8,
		...CssSelectors.allDescendants(
			{
				fill: THEME.colors.pill.blue.icon,
				cursor: 'pointer',
			},
			'svg path'
		),
	},
	removeOptionButton: {
		background: 'transparent',
		border: 'none',
		padding: '0 !important',
		margin: 0,
		cursor: 'pointer',
		color: THEME.colors.text.light,
		width: 16,
		height: 16,
		boxShadow: 'none',
		':hover': {
			background: 'transparent',
			border: 'none',
			padding: '0 !important',
			margin: 0,
			cursor: 'pointer',
		},
	},
	conditionalVisibilityQuestionDropdown: {
		flex: 1,
		minWidth: 0,
	},
	conditionalVisibilityAnswerDropdown: {
		flex: 1,
		minWidth: 0,
	},
	// The source/answer Comboboxes flex-fill their container. `width: 100%` lets
	// them grow to take the available space; long labels still truncate (the input
	// ellipsises overflow text) because the flex parent bounds the width.
	sourceComboboxInput: {
		width: '100%',
	},
	// Truncates long option labels inside a Combobox item (paired with bs.truncateText).
	comboboxItemLabel: {
		flex: 1,
		minWidth: 0,
	},
	// The icon-only "remove answer" Button rendered alongside additional answer rows.
	answerRemoveButton: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		lineHeight: 1,
		display: 'inline-flex',
		alignItems: 'center',
		color: charmGray,
	},
	// Outer wrapper for the conditional-visibility rule editor.
	conditionalVisibilityContainer: {
		padding: '10px 0',
		fontSize: THEME.typography.fontSizes.sm,
	},
	// Top-level flex row wrapping the trigger group and the answers column.
	conditionalVisibilityRow: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: spacing.sm,
		flexWrap: 'wrap',
	},
	// 'Only show when [question] is' — the checkbox, label, and question picker.
	conditionalVisibilityTriggerGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		// Grow to fill the row so the source-question combobox can use the spare
		// container width (long question titles were truncated at the old fixed 170px).
		flex: 1,
		minWidth: 0,
	},
	// Vertical stack of answer rows (first answer plus any OR'd rows).
	conditionalVisibilityAnswersColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
		// Cap the answers column at 40% so the trigger group (flex: 1) keeps the
		// larger ~60% share — otherwise the inline "Only show when"/"is" labels
		// leave the source-question combobox narrower than the answer combobox.
		flex: '0 1 40%',
		minWidth: 0,
	},
	// Row wrapping an additional (OR'd) answer alongside its '-or-' label.
	conditionalVisibilityOrRow: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
	},
	// The '-or-' label, absolutely positioned left of an additional answer row.
	conditionalVisibilityOrLabel: {
		color: charmGray,
		fontStyle: 'italic',
		position: 'absolute',
		transform: `translateX(calc(-100% - ${spacing.sm}px))`,
	},
	// Text-only '+ Add Answer' affordance.
	conditionalVisibilityAddAnswerButton: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		color: brandPrimary,
		padding: 0,
		fontSize: THEME.typography.fontSizes.sm,
		textAlign: 'left',
	},
	// A single answer row: the answer Combobox plus optional remove button.
	conditionalVisibilityAnswerRow: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		// Fill the answers column (and the "-or-" wrapper rows) so the answer
		// combobox can grow; safe in both the column (default stretch) and the
		// row contexts it renders in.
		width: '100%',
		minWidth: 0,
	},
});
